/**
 * DOCUMENTATION:
 * Component: SystemDesignCAP
 * Purpose: Teaching the trade-offs of distributed systems using the CAP Theorem.
 * * * Note for Junior Developers:
 * - Consistency (C): Every read receives the most recent write or an error.
 * - Availability (A): Every request receives a (non-error) response, without the guarantee that it contains the most recent write.
 * - Partition Tolerance (P): The system continues to operate despite an arbitrary number of messages being dropped by the network.
 * - THE REALITY: In the distributed world, network failures (Partitions) are inevitable.
 * Therefore, you are almost always choosing between Consistency and Availability.
 */

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack, Chip,
} from "@mui/material";

/* Icons */
import BalanceIcon from '@mui/icons-material/Balance';
import HubIcon from '@mui/icons-material/Hub';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

const ChoiceBox = styled(Box)(({ theme, active }) => ({
    padding: theme.spacing(3),
    borderRadius: "12px",
    backgroundColor: active ? "rgba(0, 200, 255, 0.05)" : "rgba(255, 255, 255, 0.02)",
    border: `1px solid ${active ? "#00c8ff" : "rgba(255, 255, 255, 0.1)"}`,
    textAlign: "center",
    height: "100%",
}));

export default function SystemDesignCAP() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    THE CAP THEOREM
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. The Three Pillars */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><BalanceIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Consistency vs. Availability</Typography>
                        </Box>
                        <Typography mb={4} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            The CAP Theorem states that a distributed system can only provide two of the three following guarantees at once.
                            Since <strong>Partition Tolerance (P)</strong> is a requirement for any network-based system, the real choice
                            is usually between <strong>CP</strong> and <strong>AP</strong>.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={4}>
                                <ChoiceBox>
                                    <Typography variant="h6" color="#00c8ff" fontWeight={800}>Consistency (C)</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                                        "I want the most recent data, or I'd rather have an error than old data."
                                    </Typography>
                                </ChoiceBox>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ChoiceBox>
                                    <Typography variant="h6" color="#4ade80" fontWeight={800}>Availability (A)</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                                        "I want the system to always respond, even if the data is slightly out of date."
                                    </Typography>
                                </ChoiceBox>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ChoiceBox>
                                    <Typography variant="h6" color="#ffb300" fontWeight={800}>Partition Tolerance (P)</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                                        "The system must keep working even if the network between servers fails."
                                    </Typography>
                                </ChoiceBox>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. Real-World Trade-offs */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="#00c8ff" mb={3}>Choosing Your Path</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 3, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", borderLeft: "4px solid #00c8ff" }}>
                                    <Typography variant="h6" fontWeight={700} gutterBottom>CP: Consistency + Partition Tolerance</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                        If a network partition occurs, the system shuts down the non-consistent nodes to ensure data integrity.
                                    </Typography>
                                    <Chip label="Example: Banking Systems, MongoDB" size="small" sx={{ color: "#00c8ff", borderColor: "#00c8ff" }} variant="outlined" />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 3, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", borderLeft: "4px solid #4ade80" }}>
                                    <Typography variant="h6" fontWeight={700} gutterBottom>AP: Availability + Partition Tolerance</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                        The system remains operational during a partition, but nodes may return different (stale) versions of data.
                                    </Typography>
                                    <Chip label="Example: Social Media Feeds, Cassandra" size="small" sx={{ color: "#4ade80", borderColor: "#4ade80" }} variant="outlined" />
                                </Box>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. ACID vs. BASE */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#ff6b6b" }}><ErrorOutlineIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>ACID vs. BASE</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            Traditional SQL databases focus on <strong>ACID</strong> properties (Strong Consistency).
                            Modern NoSQL systems often opt for <strong>BASE</strong> (Eventual Consistency) to achieve massive scale.
                        </Typography>

                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" color="#ff6b6b" fontWeight={700}>ACID (Strict)</Typography>
                                <Typography variant="caption" display="block" sx={{ opacity: 0.6, mb: 1 }}>Atomicity, Consistency, Isolation, Durability</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>Focuses on data being 100% correct immediately. If a transaction can't be completed perfectly, it fails entirely.</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" color="#00c8ff" fontWeight={700}>BASE (Fluid)</Typography>
                                <Typography variant="caption" display="block" sx={{ opacity: 0.6, mb: 1 }}>Basically Available, Soft State, Eventual Consistency</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>The system will eventually be consistent, but "right now" it might show you an old version of a comment or a like count.</Typography>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
